/* Small pieces the guide texts are written with. */
import { useContext, type ReactNode } from "react";
import { FeedContext } from "./feed";
import { GAMES } from "../data/games";
import { FEATURE_MATRIX } from "../data/featureMatrix";
import { planPrices } from "../hooks/useCatalog";
import type { PlanType } from "../types/games";

export const DISCORD_URL = "https://discord.gg/d64RvvK9Ta";

/** A plan's price, written into running text: "€5". */
export function Price({ game, plan }: { game: string; plan: PlanType }) {
  const feed = useContext(FeedContext);
  const g = GAMES.find((x) => x.id === game);
  if (!g) return null;
  /* A plan the feed does not list is not on sale; the bundled price is still
     the right thing to print mid-sentence rather than a blank. */
  return <>{planPrices(g, feed)[plan]?.price ?? g.pricing[plan] ?? ""}</>;
}

export const Note = ({ children }: { children: ReactNode }) => <div className="guide-note">{children}</div>;

export const Table = ({ children }: { children: ReactNode }) => (
  <div className="guide-table">
    <table>{children}</table>
  </div>
);

/* Deadlock's two builds, row by row, from the same data as the comparison
   on /features -- so the guide cannot say something the table does not. */
export function DeadlockBuildTable() {
  const rows = FEATURE_MATRIX.flatMap((g) => g.rows).filter((r) => r.has["dl-int"] || r.has["dl-ext"]);
  const cell = (v: true | "partial" | undefined) => (v === "partial" ? "Partial" : v ? "Yes" : "—");
  return (
    <Table>
      <thead>
        <tr>
          <th>&nbsp;</th>
          <th>Internal</th>
          <th>External</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>How it runs</td>
          <td>Inside the game process</td>
          <td>As a separate overlay</td>
        </tr>
        {rows.map((r) => (
          <tr key={r.label}>
            <td>{r.label}</td>
            <td>{cell(r.has["dl-int"])}</td>
            <td>{cell(r.has["dl-ext"])}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
